/* =========================================================================
 * THE SEARCH STRING — pure, and in its own file so it can be TESTED.
 *
 * It lived inside HuntWorkspace.jsx and could not be imported under node,
 * which meant the one regression this logic exists to prevent had to be
 * checked by reading the source rather than by running it. That is the same
 * class of mistake as the bug itself: a test that looks at the code instead of
 * the behaviour. `splitPlan.js`, `resolveModel.js` and `canvaGap.js` are all
 * split out for exactly this reason.
 * ========================================================================= */

/**
 * THE SEARCH, COMPOSED FROM THE RECORD.
 *
 * ⚠️ QUALIFIERS ARE DE-DUPLICATED ON WORD BOUNDARIES, and this is not
 * pedantry. The previous composer used `includes()`, so "NCHSAA 4A Football"
 * swallowed the state qualifier — **`NC` is a substring of `NCHSAA`** — and
 * the test written to catch it passed anyway by matching the NC inside NCHSAA.
 * Two-letter state codes make that a certainty rather than an edge case.
 *
 * A record with no name returns '' and the search is not offered. The name IS
 * the handle; a query of bare qualifiers ("— Football · NC") searches for
 * nothing.
 */
/**
 * ⚠️ THE BREADCRUMB IS NOT PART OF THE NAME, AND LEAVING IT IN BREAKS THE SEARCH.
 *
 * Record names carry a trailing display breadcrumb — `— Equestrian · North
 * Carolina`, `· State · Michigan` — added for the board, not for a search
 * engine. Brian pressed the button on a real record and got:
 *
 *   "USEF / EVUSA Vaulting National Championships — 2* Young Vaulter/Gold
 *    Individual Female — Equestrian · North Carolina NC champion"
 *
 * which finds nothing. The hand-hunt skill has said "strip the trailing
 * breadcrumb before parsing" since it was written; the composer just never did
 * it. The `·` separator is the reliable marker, and the em-dash segment that
 * precedes it only counts as breadcrumb when it repeats the sport or the state
 * — a genuine title can contain an em dash ("2* Young Vaulter/Gold Individual
 * Female") and must survive.
 */
export function searchableName(c) {
  let name = String(c?.name || '').trim()
  if (!name) return ''

  /* Everything from the first `·` on is breadcrumb — it is only ever appended
   * for display, never part of what a governing body calls the event. */
  name = name.split('·')[0].trim()

  /* An em-dash tail is breadcrumb ONLY if it is just the sport and/or state.
   * Anything else after a dash is part of the title and stays. */
  const sport = String(c?.sport || '').trim()
  const state = String(c?.state || '').trim()
  const parts = name.split(/\s+—\s+/)
  while (parts.length > 1) {
    const tail = parts[parts.length - 1].trim()
    const isCrumb = tail && [sport, state].filter(Boolean).some((x) => x.toLowerCase() === tail.toLowerCase())
    if (!isCrumb) break
    parts.pop()
  }
  return parts.join(' — ').replace(/[\s—·-]+$/, '').trim()
}

export function huntQuery(c) {
  const name = searchableName(c)
  if (!name) return ''
  /* ⚠️ WORD BOUNDARIES. `NC` is a substring of `NCHSAA`, so an includes() test
   * silently swallowed the state — and the assertion written to catch that
   * passed anyway by matching the NC inside NCHSAA. Two-letter state codes make
   * this a certainty rather than an edge case. */
  const has = (t) => new RegExp(`\\b${String(t).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(name)
  const year = String(c?.seasonYear || '').trim()
  /* The STATE is dropped when the record already carries a state-scoped
   * governing body or the state's full name — "North Carolina … NC" is noise
   * that narrows nothing and makes the query look broken to whoever reads it. */
  const state = String(c?.state || '').trim()
  const stateNamed = state && new RegExp(`\\b${state}\\b`, 'i').test(name)
  const extra = [c?.sport, c?.level, stateNamed ? '' : state]
    .map((x) => String(x || '').trim())
    .filter((x) => x && x !== 'NATIONAL' && !has(x))
  return [year && !has(year) ? year : '', name, ...extra, 'champion'].filter(Boolean).join(' ')
}
